/**
 * Patient /me API (api-contract.md §3.1–§3.4): profile, family, addresses, prescriptions,
 * uploads. Typed wrappers over the fetch client.
 */

import { apiFetch, fetchBlobUrl, newIdempotencyKey, rawFormFetch } from "./client";

// ----- Profile (P-03) -----

export interface MeProfile {
  id: string;
  role: string;
  full_name: string;
  email: string | null;
  phone: string | null;
  language_preference: string;
  timezone: string;
  push_master_kill: boolean;
  must_change_password: boolean;
  password_set: boolean;
}

export const getMe = (): Promise<MeProfile> => apiFetch("/api/v1/me");

export const patchProfile = (body: {
  full_name?: string;
  language_preference?: string;
  timezone?: string;
  push_master_kill?: boolean;
}): Promise<MeProfile> => apiFetch("/api/v1/me/profile", { method: "PATCH", body });

/** Supported locales for the profile language picker (INF-05). Public, config-driven. */
export interface SupportedLocales {
  default: string;
  locales: { code: string; label: string }[];
}

export const getLocales = (): Promise<SupportedLocales> => apiFetch("/api/v1/system/locales");

// ----- Family (P-05) -----

export interface FamilyMember {
  id: string;
  full_name: string;
  date_of_birth: string;
  gender: string;
  relationship: string;
  relationship_label: string | null;
  contact_phone: string | null;
  govt_id_type: string | null;
  govt_id_number_masked: string | null;
  govt_id_image_r2_key: string | null;
  govt_id_belongs_to_guardian: boolean;
  is_minor: boolean;
  active: boolean;
}

export const listFamily = (): Promise<FamilyMember[]> => apiFetch("/api/v1/me/family");

export const addFamily = (body: {
  full_name: string;
  date_of_birth: string;
  gender: string;
  relationship: string;
  relationship_label?: string | null;
  contact_phone?: string | null;
}, idempotencyKey?: string): Promise<FamilyMember> =>
  apiFetch("/api/v1/me/family", { method: "POST", body, idempotencyKey: idempotencyKey ?? newIdempotencyKey() });

export const updateFamily = (
  id: string,
  body: Partial<{ full_name: string; gender: string; relationship: string; contact_phone: string }>,
): Promise<FamilyMember> => apiFetch(`/api/v1/me/family/${id}`, { method: "PATCH", body });

export const removeFamily = (id: string): Promise<void> =>
  apiFetch(`/api/v1/me/family/${id}`, { method: "DELETE" });

export const setFamilyGovtId = (
  id: string,
  body: { govt_id_type: string; govt_id_number: string; govt_id_belongs_to_guardian?: boolean },
): Promise<FamilyMember> =>
  apiFetch(`/api/v1/me/family/${id}/govt-id`, { method: "POST", body });

// ----- Addresses (P-04) -----

export interface Address {
  id: string;
  label: string | null;
  name: string;
  phone: string;
  line1: string;
  line2: string | null;
  landmark: string | null;
  city: string;
  state_code: string;
  pin_code: string;
  is_default: boolean;
  active: boolean;
}

export const listAddresses = (): Promise<Address[]> => apiFetch("/api/v1/me/addresses");

export type AddressInput = Omit<Address, "id" | "is_default" | "active"> & { is_default?: boolean };

export const addAddress = (body: AddressInput, idempotencyKey?: string): Promise<Address> =>
  apiFetch("/api/v1/me/addresses", { method: "POST", body, idempotencyKey: idempotencyKey ?? newIdempotencyKey() });

export const updateAddress = (id: string, body: Partial<AddressInput>): Promise<Address> =>
  apiFetch(`/api/v1/me/addresses/${id}`, { method: "PATCH", body });

export const removeAddress = (id: string): Promise<void> =>
  apiFetch(`/api/v1/me/addresses/${id}`, { method: "DELETE" });

export const setDefaultAddress = (id: string): Promise<Address> =>
  apiFetch(`/api/v1/me/addresses/${id}/set-default`, { method: "POST" });

// ----- Prescriptions (P-06 / P-07) -----

export interface RxLine {
  id: string;
  medicine_id: string;
  medicine_name: string;
  schedule_class: string;
  quantity: number;
  dosage_value: string;
  dosage_unit: string;
  food_relation: string;
  time_of_day: string[];
  duration_days: number | null;
  display_order: number;
}

export interface Prescription {
  id: string;
  /** The reference people use (RX000001), not the UUID `id`. */
  prescription_id: string;
  patient_account_id: string;
  for_family_member_id: string | null;
  /** On the doctor's view only (DOC-14): who it is for — the family member's name when it is for
   *  one, else the account holder's; the holder; and their phone, last 4 digits only (D-20). */
  patient_name?: string | null;
  account_holder_name?: string | null;
  patient_phone?: string | null;
  authored_by_user_id: string | null;
  authored_by_name: string | null;
  on_behalf_of_doctor_id: string | null;
  on_behalf_of_doctor_name: string | null;
  doctor_specialisation: string | null;
  source_upload_id: string | null;
  is_digitized: boolean;
  notes: string | null;
  schedule_class: string;
  issued_at: string;
  expires_at: string;
  is_consumed: boolean;
  status: string; // active | expired | consumed
  /** False once staff deactivate it: hidden from the list, but a link still opens it. */
  is_active: boolean;
  paper_original_held: boolean;
  is_editable: boolean;
  lines: RxLine[];
  created_at: string;
}

export interface PrescriptionSummary {
  id: string;
  /** The reference people use (RX000001), not the UUID `id`. */
  prescription_id: string;
  for_family_member_id: string | null;
  schedule_class: string;
  notes: string | null;
  issued_at: string;
  expires_at: string;
  is_consumed: boolean;
  status: string; // active | expired | consumed
  /** False once staff deactivate it. */
  is_active: boolean;
  /** A pharmacist digitised it from the patient's paper upload. */
  is_digitized: boolean;
  line_count: number;
  /** On the doctor's list only: who it is for and their phone, last 4 digits (DOC-15, D-20). */
  patient_name?: string | null;
  patient_phone?: string | null;
}

export interface PaginatedPrescriptions {
  data: PrescriptionSummary[];
  total: number;
  page: number;
  per_page: number;
  has_more: boolean;
}

export const listPrescriptions = (query: {
  family_member_id?: string;
  page?: number;
  per_page?: number;
} = {}): Promise<PaginatedPrescriptions> => {
  const p = new URLSearchParams();
  if (query.family_member_id) p.set("family_member_id", query.family_member_id);
  p.set("page", String(query.page ?? 1));
  p.set("per_page", String(query.per_page ?? 25));
  return apiFetch(`/api/v1/me/prescriptions?${p.toString()}`);
};

export const getPrescription = (id: string): Promise<Prescription> =>
  apiFetch(`/api/v1/me/prescriptions/${id}`);

// ----- Uploads (P-08) -----

export interface Upload {
  id: string;
  for_family_member_id: string | null;
  image_r2_key: string;
  note: string | null;
  status: string;
  reject_reason: string | null;
  reject_note: string | null;
  digitized_prescription_id: string | null;
  created_at: string;
}

export interface PaginatedUploads {
  data: Upload[];
  total: number;
  page: number;
  per_page: number;
  has_more: boolean;
}

export const listUploads = (page = 1, per_page = 25): Promise<PaginatedUploads> =>
  apiFetch(`/api/v1/me/prescription-uploads?page=${page}&per_page=${per_page}`);

export async function uploadPrescription(
  file: File,
  opts: { for_family_member_id?: string; note?: string; idempotencyKey?: string } = {},
): Promise<Upload> {
  const form = new FormData();
  form.append("file", file);
  if (opts.for_family_member_id) form.append("for_family_member_id", opts.for_family_member_id);
  if (opts.note) form.append("note", opts.note);
  return rawFormFetch("/api/v1/me/prescription-uploads", form, {
    idempotencyKey: opts.idempotencyKey ?? newIdempotencyKey(),
  });
}

export const cancelUpload = (id: string): Promise<void> =>
  apiFetch(`/api/v1/me/prescription-uploads/${id}/cancel`, { method: "POST" });

/** Fetch the uploaded file as a displayable object URL (auth header can't ride a plain src). */
export const fetchUploadImage = (id: string): Promise<{ url: string; contentType: string; filename: string }> =>
  fetchBlobUrl(`/api/v1/me/prescription-uploads/${id}/image`);
